import { useMemo, type ReactElement, useEffect } from 'react'
import { Grid, Box, Button, Tooltip } from '@mui/material'
import 'dayjs/locale/en-gb'

import { H2White, Body2White, H1 } from '@/components/typography'
import css from './styles.module.css'
// import ReceiveComponent from '@/components/sidebar/QrCodeButton/ReceiveComponent'

import { useAppDispatch, useAppSelector } from '@/store'
// import { setActiveStep } from '@/store/setUpInheritorsSlice'
import { formatCurrency } from '@/utils/formatNumber'
import { useVisibleBalances } from '@/hooks/useVisibleBalances'
import { selectCurrency } from '@/store/settingsSlice'
// import { SafeTxContext } from '@/components/tx-flow/SafeTxProvider'
import ModalButton from '@/components/common/ModalButton'
import ConfirmInheritance from './ConfirmInheritance'
import ScanQrCode from '@/components/sidebar/QrCodeButton/ScanQrCode'
import useDesktop from '@/hooks/useDesktop'
import SendProcessing from '@/components/inheritors/SendProcessing'

import { setStep3Done, setActiveStep, type InheritanceStep } from '@/store/setUpInheritorsV2Slice'
import Assets from '@/components/dashboard/Assets/Assets'
import MuiLink from '@mui/material/Link'

const PrepareInheritance = ({
  chainIdAddress,
  inheritanceStep,
}: {
  chainIdAddress: string
  inheritanceStep: InheritanceStep
}): ReactElement => {
  const isDesktop = useDesktop()
  const dispatch = useAppDispatch()
  const currency = useAppSelector(selectCurrency)
  const { balances, loading: balancesLoading } = useVisibleBalances()
  const fiatTotal = useMemo(
    () => (balances.fiatTotal ? formatCurrency(balances.fiatTotal, currency) : ''),
    [currency, balances.fiatTotal],
  )
  const numTokens = useMemo(() => balances.items.filter((token) => token.balance !== '0').length, [balances])

  useEffect(() => {
    if (numTokens > 0) {
      dispatch(setStep3Done({ chainIdAddress, isDone: true }))
    }
  }, [numTokens])

  const handleBack = () => {
    dispatch(setActiveStep({ chainIdAddress, activeStep: 1 }))
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} my="16px">
        <Grid container>
          <Grid item xs={5} sm={4}>
            <Button
              variant="text"
              sx={{
                paddingY: '10px',
                width: '100%',
                borderRadius: '4px',
                background: 'var(--Ebony-Clay-950, #2F354B)',
                color: 'var(--Ebony-Clay-300, #D2D7E5)',
                ':hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                },
              }}
              onClick={handleBack}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={2} sm={4}></Grid>
          <Grid item xs={5} sm={4} textAlign="right">
            {numTokens === 0 && (
              <Button
                disabled={numTokens === 0}
                variant="contained"
                sx={{
                  paddingY: '10px',
                  width: '100%',
                }}
              >
                Set up
              </Button>
            )}
            {numTokens > 0 && (
              <ModalButton
                titleModal={'Confirm inheritance'}
                contentModal={
                  <SendProcessing>
                    <ConfirmInheritance inheritanceStep={inheritanceStep} />
                  </SendProcessing>
                }
              >
                <Button
                  disabled={numTokens === 0}
                  variant="contained"
                  sx={{
                    paddingY: '10px',
                    width: '100%',
                  }}
                >
                  Set up
                </Button>
              </ModalButton>
            )}
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid
      container
      sx={
        isDesktop
          ? {
              borderRadius: '4px',
              border: '1px solid var(--Ebony-Clay-950, #2F354B)',
              padding: '16px',
            }
          : {}
      }
    >
      <Grid item container xs={12} mb="16px">
        <Grid item container xs={12} flexDirection="column" justifyContent="center">
          <H1>3. Prepare inheritance</H1>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid
          container
          px="16px"
          py="8px"
          sx={{
            borderRadius: '4px',
            border: '1px solid var(--Ebony-Clay-950, #2F354B)',
          }}
          mb="16px"
        >
          <Grid item xs={12}>
            <Grid item container xs={12}>
              <Grid item xs="auto">
                <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                  Total asset value of {numTokens === 0 ? '0 token' : ''}{' '}
                </Body2White>
              </Grid>
              {numTokens > 0 && (
                <Grid item container xs textAlign="left">
                  <ModalButton titleModal={'Assets'} contentModal={<Assets showTitle={false} isModal={true} />}>
                    <Tooltip title="Show list of tokens" placement="bottom">
                      <MuiLink
                        component="button"
                        sx={{
                          marginLeft: '4px',
                          fontSize: '14px',
                          color: 'var(--Blue-Ribbon-600, #5D5DFF)',
                          textDecorationColor: 'var(--Blue-Ribbon-600, #5D5DFF)',
                          '&:hover': {
                            color: 'var(--Blue-Ribbon-700, #4536F5)',
                          },
                        }}
                        underline="always"
                      >
                        {numTokens} {numTokens > 1 ? 'tokens' : 'token'}
                      </MuiLink>
                    </Tooltip>
                  </ModalButton>
                </Grid>
              )}
            </Grid>
            <H2White>
              ${fiatTotal.replace('USD', '')}{' '}
              <span style={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', fontWeight: 300 }}>USD</span>
            </H2White>
          </Grid>
          {/* <Grid item xs={2}></Grid> */}
        </Grid>

        <Box className={css.prepareInheritanceScroll}>
          {/* <Body2White>
            Enhance your inheritance for your loved ones by adding more funds. Choose one of the 3 convenient options
            below:
          </Body2White> */}
          {/* <ReceiveComponent /> */}
          <ScanQrCode />
          {/* <Box
            mt="16px"
            p="16px"
            sx={{
              borderRadius: '4px',
              border: '1px solid var(--Ebony-Clay-950, #2F354B)',
            }}
          >
            <Body2White sx={{ color: 'var(--Supporting-Koromiko, #FC8313)' }}>
              Please note before the rules are met:
            </Body2White>
            <Body2White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)' }}>
              You can totally send or grow your assets any time via well known DeFi protocols such as swap, lending,
              liquid staking, etc.{' '}
            </Body2White>
          </Box> */}
        </Box>
      </Grid>
      {renderFooter()}
    </Grid>
  )
}

export default PrepareInheritance
